import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { chatAsk, listDeals, type ChatTurn } from '../lib/api.js';
import { Spinner } from './ui.js';

/**
 * Floating AI chat assistant. Starts scoped to `dealId` if given (per-deal), else portfolio-wide.
 * A context switcher lets the analyst change which deal (or "all deals") they're asking about
 * without leaving the chat. Assistant replies are rendered as light markdown (bold / bullets /
 * headings) for readability. Answers are grounded server-side in the fund's own data.
 */

/** Inline markdown: **bold** → <strong>. */
function inline(s: string): ReactNode {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-navy">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>,
  );
}

/** Light markdown block renderer: headings, bullet lists, numbered lists, paragraphs. */
function renderRich(text: string): ReactNode {
  const lines = text.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  const flush = (key: string) => {
    if (bullets.length) {
      blocks.push(<ul key={key} className="list-disc pl-4 space-y-1 my-1.5">{bullets.map((b, i) => <li key={i}>{inline(b)}</li>)}</ul>);
      bullets = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.replace(/\s+$/, '');
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/) || line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet) { bullets.push(bullet[1] ?? ''); return; }
    flush(`ul${i}`);
    if (!line.trim()) return;
    const heading = line.match(/^\s*#{1,4}\s+(.*)$/);
    if (heading) { blocks.push(<div key={i} className="font-bold text-navy mt-2 mb-0.5">{inline(heading[1] ?? '')}</div>); return; }
    blocks.push(<p key={i} className="my-1 leading-relaxed">{inline(line)}</p>);
  });
  flush('ulEnd');
  return <div className="space-y-0.5">{blocks}</div>;
}

export function ChatWidget({ dealId, label }: { dealId?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [activeDealId, setActiveDealId] = useState<string | undefined>(dealId);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);

  // Deals for the context switcher (cached — same query key the deals list uses).
  const { data: dealsData } = useQuery({ queryKey: ['deals'], queryFn: listDeals, staleTime: 60_000, enabled: open });
  const deals = dealsData?.deals ?? [];

  // Reset the scope to the current page's deal on navigation; clear the convo on any scope change.
  useEffect(() => { setActiveDealId(dealId); }, [dealId]);
  useEffect(() => { setTurns([]); setErr(''); }, [activeDealId]);
  useEffect(() => { if (open) bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }); }, [turns, busy, open]);

  const activeLabel = activeDealId
    ? (deals.find((d) => d.id === activeDealId)?.company ?? label ?? 'this deal')
    : 'all deals';
  const scope = activeDealId ? `about ${activeLabel}` : 'across all deals';
  const suggestions = activeDealId
    ? ['Summarise this deal', 'What are the key risks?', 'Why might we pass?']
    : ['How many deals came in the last 2 weeks?', 'Which deals did we reject and why?', 'What’s in diligence right now?'];

  async function send(q: string) {
    const question = q.trim();
    if (!question || busy) return;
    const history = turns.slice(-6).map(({ role, content }) => ({ role, content }));
    setTurns((t) => [...t, { role: 'user', content: question }]);
    setDraft(''); setBusy(true); setErr('');
    try {
      const { answer, usedDeal } = await chatAsk(question, { dealId: activeDealId, history });
      setTurns((t) => [...t, { role: 'assistant', content: answer, usedDeal }]);
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Ask the AI assistant"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-navy text-white shadow-sh2 hover:bg-navy-2 text-[13px] font-semibold">
        <span className="text-[15px]">💬</span> Ask AI
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-40 w-[min(440px,calc(100vw-2.5rem))] h-[min(600px,calc(100vh-6rem))] flex flex-col bg-card border border-line rounded-card shadow-sh2 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-line bg-canvas">
        <div className="flex items-center justify-between">
          <div className="text-[12.5px] font-bold text-navy">Krit assistant</div>
          <button onClick={() => setOpen(false)} className="text-muted-2 hover:text-navy text-[16px] leading-none px-1">×</button>
        </div>
        {/* Just name a company in your question — the assistant finds the deal. The picker only
            sets a DEFAULT for when you don't name one (Arpit's "focus on this deal" behaviour). */}
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="text-[10.5px] text-muted-2 whitespace-nowrap">Default deal:</span>
          <select value={activeDealId ?? ''} onChange={(e) => setActiveDealId(e.target.value || undefined)}
            className="flex-1 min-w-0 px-2 py-1 border border-line rounded-lg text-[11.5px] bg-card text-navy">
            <option value="">All deals (portfolio)</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.short} — {d.company}</option>)}
          </select>
        </div>
      </div>

      <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {turns.length === 0 && (
          <div className="text-[12px] text-muted">
            <p className="mb-2">Ask me anything {scope} — just name a company in your question (e.g. “In Greenjoules, what does the business do?”) and I’ll pull up that deal. I only answer from the deal data, and I’ll say so if I don’t know.</p>
            <div className="flex flex-col gap-1.5">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} className="text-left px-2.5 py-1.5 rounded-lg border border-line-2 text-[12px] text-navy hover:bg-canvas">{s}</button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={t.role === 'user' ? 'text-right' : ''}>
            <div className={`inline-block max-w-[88%] px-3 py-2 rounded-xl text-[12.5px] text-left ${t.role === 'user' ? 'bg-navy text-white whitespace-pre-wrap' : 'bg-canvas text-navy border border-line-2'}`}>
              {t.role === 'assistant' ? renderRich(t.content) : t.content}
            </div>
            {/* Which deal the answer was grounded in — transparent when detection picked a deal
                other than the default (or when asking portfolio-wide answered about one deal). */}
            {t.role === 'assistant' && t.usedDeal && (
              <div className="mt-0.5 text-[10px] text-muted-2">↳ answered about {t.usedDeal}</div>
            )}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-[12px] text-muted"><Spinner size={14} /> Thinking…</div>}
        {err && <div className="text-[11.5px] text-red">{err}</div>}
      </div>

      <div className="p-2.5 border-t border-line flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(draft); }}
          placeholder={`Ask about ${activeLabel}…`} disabled={busy}
          className="flex-1 min-w-0 px-3 py-2 border border-line rounded-lg text-[13px] outline-none focus:border-teal focus:ring-2 focus:ring-teal/15 disabled:opacity-60" />
        <button onClick={() => send(draft)} disabled={busy || !draft.trim()} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">Send</button>
      </div>
    </div>
  );
}
